/**
 * ry-ui Icon Registry
 *
 * One canonical name vocabulary (Fomantic). Two visual sets — `fomantic`
 * (default) and `lucide`. Each set is shipped as a separate side-effect
 * import:
 *
 *   import 'ry-ui/icons/fomantic';   // default look — pre-imported by ry-ui.js
 *   import 'ry-ui/icons/lucide';     // swap to Lucide-style glyphs
 *
 * Sets are populated at import time via `registerSet()`. `setIconSet(name)`
 * flips which one `<ry-icon>` renders from. There is NO cross-set fallback:
 * if a name is not registered in the active set, the icon renders empty.
 *
 * Consumers extend the registry with:
 *   - `registerIcon(name, svg)` — writes to BOTH sets (available regardless
 *     of which is active). Good default for custom app icons.
 *   - `registerSet(set, icons)` — writes to one set only. Used by the
 *     `icons/*` side-effect modules and by anyone shipping a visually
 *     distinct alt set.
 *
 * Usage:
 *   import { getIcon, setIconSet, registerIcon } from 'ry-ui';
 *   getIcon('home');                          // current set's home glyph
 *   registerIcon('logo', '<svg>…</svg>');     // your custom icon
 *   setIconSet('lucide');                     // requires lucide.ts imported
 */

export type IconSet = 'fomantic' | 'lucide';

export const SETS: Record<IconSet, Record<string, string>> = {
  fomantic: {},
  lucide: {},
};

let activeSet: IconSet = 'fomantic';

/** Switch the active icon set. Re-renders any mounted <ry-icon>. */
export function setIconSet(set: IconSet): void {
  if (set === activeSet) return;
  activeSet = set;
  document.querySelectorAll('ry-icon').forEach((el) => {
    (el as unknown as { rerender?: () => void }).rerender?.();
  });
}

/** Read the active icon set. */
export function getIconSet(): IconSet {
  return activeSet;
}

/**
 * Get the rendered HTML for an icon name from the active set.
 * Returns '' if the name isn't registered in the active set — no
 * cross-set fallback.
 */
export function getIcon(name: string): string {
  return SETS[activeSet][name] ?? '';
}

/**
 * Register or override a single icon in BOTH sets, so it's visible
 * regardless of which set is active. Good default for custom app icons.
 */
export function registerIcon(name: string, svg: string): void {
  SETS.fomantic[name] = svg;
  SETS.lucide[name] = svg;
}

/** Bulk version of registerIcon — writes to both sets. */
export function registerIcons(icons: Record<string, string>): void {
  Object.assign(SETS.fomantic, icons);
  Object.assign(SETS.lucide, icons);
}

/**
 * Register icons into ONE specific set. Used by the per-set side-effect
 * modules (`icons/fomantic.ts`, `icons/lucide.ts`) and by callers who want
 * to ship a visually-distinct alt set.
 */
export function registerSet(set: IconSet, icons: Record<string, string>): void {
  Object.assign(SETS[set], icons);
}

/** Get all canonical icon names available in the active set. */
export function getIconNames(): string[] {
  return Object.keys(SETS[activeSet]);
}
